import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";

const EMPTY = { label: "", address: "", phone: "", hours: "" };

function LocationRow({ loc, onToggle }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex items-start gap-3" data-testid={`location-row-${loc.location_id}`}>
      <MapPin className="w-4 h-4 text-pink-300 mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-bold">{loc.label}</span>
          {loc.is_primary && <span className="inline-flex items-center gap-1 text-[10px] uppercase text-amber-300"><Star className="w-3 h-3" /> Primary</span>}
        </div>
        {loc.address && <p className="text-xs text-zinc-400">{loc.address}</p>}
        {(loc.phone || loc.hours) && <p className="text-[11px] text-zinc-500">{[loc.phone, loc.hours].filter(Boolean).join(" · ")}</p>}
      </div>
      {!loc.is_primary && (
        <label className="flex items-center gap-2 text-[11px] text-zinc-400">
          {loc.is_active !== false ? "Open" : "Off"}
          <Switch
            checked={loc.is_active !== false}
            onCheckedChange={(v) => onToggle(loc, v)}
            aria-label={`${loc.label} accepting orders`}
            data-testid={`location-toggle-${loc.location_id}`}
          />
        </label>
      )}
    </div>
  );
}

export default function ShopLocations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const shopId = user ? `shop_${user.user_id}` : null;
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!shopId) return;
    try {
      const { data } = await api.get(`/shops/${shopId}/locations/manage`);
      setRows(data.locations || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load locations"); }
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (form.label.trim().length < 2) { toast.error("Give the location a name"); return; }
    setBusy(true);
    try {
      await api.post(`/shops/${shopId}/locations`, form);
      setForm(EMPTY);
      toast.success("Location added");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add location"); }
    finally { setBusy(false); }
  };

  const toggle = async (loc, active) => {
    try {
      await api.patch(`/shops/${shopId}/locations/${loc.location_id}`, { is_active: active });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update"); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="shop-locations-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="shop-locations-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <h1 className="text-4xl font-black tracking-tighter">Locations</h1>
        <p className="text-sm text-zinc-400 mt-1">With two or more active locations, customers pick one on your storefront before ordering.</p>
      </div>
      <div className="space-y-2">
        {rows.map((loc) => <LocationRow key={loc.location_id} loc={loc} onToggle={toggle} />)}
      </div>
      <form onSubmit={add} className="rounded-3xl bg-white/[0.03] border border-white/10 p-5 space-y-3" data-testid="location-add-form">
        <h2 className="font-black inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add a location</h2>
        <Input placeholder="Name (e.g. Downtown)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="location-input-label" />
        <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="location-input-address" />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="location-input-phone" />
          <Input placeholder="Hours (e.g. 11am–9pm)" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="location-input-hours" />
        </div>
        <Button type="submit" disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white" data-testid="location-add-submit">
          Add location
        </Button>
      </form>
    </div>
  );
}
